import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ByzzerChangeEventHandler, ByzzerSelect } from '@byzzer/ui-components';
import classnames from 'classnames';
import {useTenantApi} from '@/hooks/useTenantApi';
import { charsToBeFilteredOut, specificCharsExclusionReportSkus } from '@/config/globalVars';
import {
    additionalCharacteristicsDimensions,
    additionalCharDimensionSkus,
    productLevelsHigherThanCategory,
} from '@/config/reportSelectorVars.config';
import { CharacteristicDimension } from '@/types/ReportRun';
import { useApp } from '@/contexts/UserContext';
import { SelectorLabelInfo } from '../SelectorLabelInfo';

const baseClassName = 'characteristics-dimention-select';

export type CharacteristicsDimension = CharacteristicDimension[]; // need to check type to align with ProductRunConfigFilters

export type CharacteristicsDimensionSelectProps = {
    name?: string;
    value?: CharacteristicDimension[];
    categories?: string[];
    onChange?: ByzzerChangeEventHandler<CharacteristicDimension[]>;
    maxSelections?: number;
    includeCustomCharacteristics?: boolean;
    allowClear?: boolean;
    sku?: string;
    productLevel?: string;
    label?: ReactNode;
    required?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>;

export function CharacteristicsDimensionSelect({
    className,
    onChange,
    value,
    name,
    categories,
    maxSelections,
    includeCustomCharacteristics = true,
    allowClear,
    label,
    sku = '',
    productLevel = 'Category',
    required,
    ...props
}: CharacteristicsDimensionSelectProps) {
    const [characteristicsOption, setCharacteristicsOption] = useState<CharacteristicDimension[]>([]);
    const basicCharacteristics = useRef<CharacteristicDimension[]>([]);
    const fetchVersionRef = useRef(0);
    const fetchTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const { customCharacteristics: customCharacteristicsContext } = useApp();
    const CHAR_DIM_LABEL= (
        <>
            <p>
                <b>This report includes fields for characteristics that you get to define.</b>
            </p>
            <span>
                Choose {maxSelections} characteristic(s) to show in your report. Category, Subcategory, and Brand will
                always be included by default.
            </span>
            <span>
                The dimension you choose will break the category down into comparative groups based on that
                characteristic. Do you want to compare different brands, or the performance of different features such
                as flavor or pack type?
            </span>
        </>
    ); 
    const { getCharacteristicsForCategories } = useTenantApi();
    // todo - added functionality to accept a prop and set an internalValue using it

    // Derive selected characteristics from value prop — no duplicate state needed
    const selectedCharacteristics = useMemo(() => value ?? [], [value]);

    const updateCharacteristicsContext = useCallback(() => {
        let matchingCustomCharacteristics: CharacteristicDimension[] = [];

        if (includeCustomCharacteristics) {
            matchingCustomCharacteristics = customCharacteristicsContext
                .filter((item) => categories?.map((category) => category)?.some((i) => item?.categories?.includes(i)))
                .map((customCharacteristic) => ({
                    characteristicsCode: String(customCharacteristic.id),
                    characteristic: `${customCharacteristic.label} (Custom)`,
                    isCustom: true,
                }));
        }
        let characteristicsMap: CharacteristicDimension[] = [
            ...matchingCustomCharacteristics,
            ...basicCharacteristics.current,
        ];

        if (specificCharsExclusionReportSkus.includes(sku)) {
            if (charsToBeFilteredOut.length > 0) {
                characteristicsMap = characteristicsMap?.filter(
                    (c) => !charsToBeFilteredOut.includes(c.characteristic)
                );
            }
        }
        if (
            additionalCharDimensionSkus?.includes(sku) &&
            (productLevelsHigherThanCategory?.includes(productLevel) || (categories && categories?.length > 1))
        ) {
            characteristicsMap = [...characteristicsMap, ...additionalCharacteristicsDimensions];
        }
        setCharacteristicsOption(characteristicsMap);
    }, [includeCustomCharacteristics, customCharacteristicsContext, categories, sku, productLevel]);

    useEffect(() => {
        updateCharacteristicsContext();
    }, [includeCustomCharacteristics]);

    // Debounced fetch with stale response handling — waits 300ms after last category change
    useEffect(() => {
        const currentVersion = ++fetchVersionRef.current;

        if (fetchTimerRef.current) {
            clearTimeout(fetchTimerRef.current);
        }

        if (!categories?.length) {
            basicCharacteristics.current = [];
            updateCharacteristicsContext();
            return;
        }

        fetchTimerRef.current = setTimeout(async () => {
            try {
                const characteristicsForCategories = await getCharacteristicsForCategories(categories);

                // Discard if categories changed while request was in-flight
                if (currentVersion !== fetchVersionRef.current) return;

                const basicCharacteristicsValue: CharacteristicDimension[] = characteristicsForCategories.map(
                    (characteristic) => ({
                        characteristicsCode: characteristic.code,
                        characteristic: characteristic.displayName,
                        isCustom: false,
                    })
                );
                basicCharacteristics.current = basicCharacteristicsValue;
                updateCharacteristicsContext();
            } catch (error) {
                if (currentVersion !== fetchVersionRef.current) return;
                console.error('Error fetching characteristics:', error);
            }
        }, 300);

        return () => {
            if (fetchTimerRef.current) {
                clearTimeout(fetchTimerRef.current);
            }
        };
    }, [categories]);

    function handleChange(e: ByzzerChangeEvent<string | string[] | null | undefined>) {
        const value = characteristicsOption.filter((characteristic) => {
            return Array.isArray(e.value)
                ? e.value?.includes(characteristic.characteristicsCode)
                : characteristic.characteristicsCode === e.value;
        });

        if (onChange) {
            onChange({
                name,
                value,
            });
        }
    }

    return (
        <div className={classnames(baseClassName, className)} {...props}>
            {Number(sku) ? (
                <SelectorLabelInfo
                    sku={sku}
                    selectorCode={'characteristicDimensions'}
                    required={required}
                    includeSuffix={!required}
                    max={maxSelections}
                    isLabelBold={true}
                />
            ) : (
                CHAR_DIM_LABEL
            )}

            <ByzzerSelect
                name={name}
                value={selectedCharacteristics.map((characteristic) => characteristic.characteristicsCode)}
                onChange={handleChange}
                label={label}
                options={characteristicsOption?.map((c) => ({
                    display: c.characteristic,
                    value: c.characteristicsCode,
                }))}
                placeholder={'Select from the list'}
                allowClear={allowClear}
                maxSelections={Number(maxSelections) > 0 ? maxSelections : undefined}
                allowMultiple={Number(maxSelections) > 0} // greater than zero means they set a max, would be undefined otherwise.
            />
        </div>
    );
}

export default CharacteristicsDimensionSelect;
